import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useConfirmEmail from 'features/myVaro/hooks/useConfirmEmail';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import theme from 'shared/styles/theme';

const ConfirmEmailScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'confirmEmail',
  });

  const {
    fetching,
    resendLinkOnEmail,
    isDialogVisible,
    setIsDialogVisible,
    resendCount,
  } = useConfirmEmail();
  const { updatedEmailId } = useContext(MyVaroContext);
  const description = translatedText('description', {
    emailId: updatedEmailId ?? '',
  });
  const insets = useSafeAreaInsets();

  const resetNavigation = () => {
    router.dismiss(3);
    router.replace('/myvaro/personal-details');
  };

  useBackHandler(resetNavigation);

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              options={{
                headerShown: true,
                headerBackVisible: true,
                title: translatedText('title'),
              }}
              primaryOnPress={resetNavigation}
            />
          ),
        }}
      />
      <View style={styles.separator}>
        <TitleLgText numberOfLines={0}>
          {translatedText('screenTitle')}
        </TitleLgText>
        <BodyBaseText numberOfLines={0} style={styles.row}>
          {description}
        </BodyBaseText>
      </View>
      <View
        style={[
          styles.separator,
          { paddingBottom: insets.bottom + theme.spacing.s },
        ]}
      >
        <Button
          onPress={() => resetNavigation()}
          label={translatedText('buttons.done')}
        />
        <Button
          label={translatedText('buttons.resend')}
          onPress={resendLinkOnEmail}
          isLoading={fetching}
          size="LARGE"
          type="SECONDARY"
          width="FULL"
          analyticsId="resendEmailLinkClicked"
          customAttributes={{
            verification_type: 'email',
            verification_attempt: resendCount.toString(),
          }}
        />
      </View>
      <Dialog
        isDialogVisible={isDialogVisible}
        setModalVisible={setIsDialogVisible}
        titleText={translatedText('dialog.title')}
        messageText={translatedText('dialog.message')}
        dialogButtonProps={[
          {
            label: translatedText('dialog.buttons.ok'),
            onClick: () => {
              setIsDialogVisible(false);
            },
          },
        ]}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
  row: {
    flexDirection: 'row',
  },
  separator: {
    gap: theme.spacing.m,
  },
});

export default ConfirmEmailScreen;
